import CopyToClipboardIcon from '@components/Icon/Copy';
import JobIcon from '@components/Icon/Job';
import MapMarker from '@components/Icon/MapMarker';
import { DataTable, EmptyState, useContainerHeight } from '@components/DataTable';
import { DataTableColumn } from '@components/DataTable/types';
import { Avatar, Box, Collapse, IconButton, Menu, MenuItem, Skeleton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { DELETE_JOB_OFFER_TEMPLATE, GET_JOB_OFFER_TEMPLATES } from '@gql/jobOfferTemplates';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';

interface JobOfferTemplate {
    _id: string;
    title: string;
    city?: string;
    jobNumber?: string;
    createdDate?: string;
    contract?: {
        contractType?: string;
    };
    pro?: {
        _id: string;
        commercialName?: string;
        brandLogo?: { url: string };
    };
    brand?: {
        brandName?: string;
        brandLogo?: { url: string };
    };
}

interface JobOfferTemplateDataTableProps {
    data: JobOfferTemplate[];
    loading: boolean;
    currentPage: number;
    pageSize: number;
    totalRows: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onDeleted?: () => void;
}

const TemplateSkeleton = () => (
    <TableRow>
        <TableCell sx={{ padding: '6px 8px', minWidth: 300 }}>
            <Skeleton variant="text" width="60%" height={20} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }}>
            <Skeleton variant="text" width="80%" height={20} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }}>
            <Skeleton variant="text" width="70%" height={20} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }}>
            <Skeleton variant="text" width="80%" height={20} />
        </TableCell>
        <TableCell sx={{ padding: '6px 8px' }} align="center">
            <Skeleton variant="circular" width={28} height={28} sx={{ mx: 'auto' }} />
        </TableCell>
    </TableRow>
);

const TemplateRowActions: React.FC<{ row: JobOfferTemplate; onDeleted?: () => void }> = ({ row, onDeleted }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [deleteTemplate, { loading: deleting }] = useMutation(DELETE_JOB_OFFER_TEMPLATE, {
        refetchQueries: [GET_JOB_OFFER_TEMPLATES],
        onCompleted: () => {
            showSnackbar('Modèle supprimé avec succès', 'success');
            onDeleted?.();
        },
        onError: (error) => {
            showSnackbar(error.message, 'error');
        },
    });

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => setAnchorEl(null);

    const handleEdit = () => {
        handleClose();
        navigate(`/job-offer-templates/${row.jobNumber}/detail`);
    };

    const handleCreateJobOffer = () => {
        handleClose();
        navigate(`/job-offers/add?fromTemplate=${row.jobNumber}`);
    };

    const handleDelete = async () => {
        handleClose();
        if (window.confirm(`Supprimer le modèle "${row.title}" ?`)) {
            await deleteTemplate({ variables: { _id: row._id } });
        }
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                sx={{ p: 0.5 }}
                aria-label="actions"
            >
                <i className="fi fi-br-menu-dots-vertical" style={{ fontSize: 14 }} />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleEdit}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="fi fi-rr-pencil" style={{ fontSize: 14 }} />
                        <Typography variant="body2">Modifier</Typography>
                    </Box>
                </MenuItem>
                <MenuItem onClick={handleCreateJobOffer}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="fi fi-rr-briefcase" style={{ fontSize: 14 }} />
                        <Typography variant="body2">Créer une offre</Typography>
                    </Box>
                </MenuItem>
                <MenuItem onClick={handleDelete} disabled={deleting}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="fi fi-rr-trash" style={{ fontSize: 14, color: theme.palette.errorRed.main }} />
                        <Typography variant="body2" sx={{ color: theme.palette.errorRed.main }}>
                            Supprimer
                        </Typography>
                    </Box>
                </MenuItem>
            </Menu>
        </>
    );
};

const JobOfferTemplateDataTable: React.FC<JobOfferTemplateDataTableProps> = ({
    data,
    loading,
    currentPage,
    pageSize,
    totalRows,
    onPageChange,
    onPageSizeChange,
    onDeleted,
}) => {
    const { containerRef, containerHeight } = useContainerHeight();
    const [copied, setCopied] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const handleCopy = useCallback(async (value: string, id: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setCopiedId(id);
            setTimeout(() => {
                setCopied(false);
                setCopiedId(null);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    }, []);

    const columns: DataTableColumn<JobOfferTemplate>[] = useMemo(() => [
        {
            id: 'title',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Titre du modèle</Typography>,
            field: 'title',
            minWidth: 300,
            sortable: true,
            resizable: true,
            sticky: true,
            renderCell: ({ row, isExpanded, toggleExpanded }) => {
                return (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                minWidth: 0,
                                width: '100%',
                                mb: isExpanded ? 1 : 0,
                            }}>
                            <IconButton
                                size="small"
                                onClick={toggleExpanded}
                                sx={{ p: 0.5 }}
                            >
                                {isExpanded ? <i className="fi fi-br-caret-down" /> : <i className="fi fi-br-caret-right" />}
                            </IconButton>
                            <Typography
                                component={Link}
                                to={`/job-offer-templates/${row.jobNumber}/detail`}
                                variant="h5"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    flex: 1,
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    '&:hover': { textDecoration: 'underline' },
                                }}
                            >
                                {row.title}
                            </Typography>
                        </Box>

                        <Collapse in={isExpanded} timeout={300} unmountOnExit>
                            <Box sx={{ pb: 1, pl: 4.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: 0.75 }}>
                                    <Avatar
                                        src={row.pro?.brandLogo?.url || '/default-logo.png'}
                                        alt={row.pro?.commercialName || 'Company Logo'}
                                        sx={{ width: '17px', height: '17px' }}
                                    />
                                    <Typography
                                        sx={{
                                            fontWeight: 400,
                                            fontSize: '14px',
                                            lineHeight: 'normal',
                                            color: theme => theme.palette.textSecondary?.main,
                                        }}>
                                        {row.pro?.commercialName}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            color: theme => theme.palette.textSecondary?.main,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                        }}>
                                        {row.jobNumber}{' '}
                                        <Tooltip open={copied && copiedId === row._id} title="Copy to clipboard" placement="top">
                                            <Box sx={{ width: '10px', height: '12px', display: 'flex', alignItems: 'center' }}>
                                                <CopyToClipboardIcon
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleCopy(row.jobNumber || '', row._id)}
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: '12px', mt: 0.75 }}>
                                    {row.contract?.contractType && (
                                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <JobIcon />
                                            <Typography
                                                sx={{
                                                    color: theme => theme.palette.textSecondary?.main,
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                }}>
                                                {row.contract.contractType}
                                            </Typography>
                                        </Box>
                                    )}
                                    {row.city && (
                                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <MapMarker />
                                            <Typography
                                                sx={{
                                                    color: theme => theme.palette.textSecondary?.main,
                                                    fontWeight: 600,
                                                    fontSize: '14px',
                                                }}>
                                                {row.city}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Collapse>
                    </Box>
                );
            },
        },
        {
            id: 'company',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Entreprise</Typography>,
            field: 'pro',
            minWidth: 180,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                        src={row.pro?.brandLogo?.url || '/default-logo.png'}
                        alt={row.pro?.commercialName || 'Company Logo'}
                        sx={{ width: '20px', height: '20px' }}
                    />
                    <Typography variant="body2" sx={{ color: theme => theme.palette.textSecondary?.main }}>
                        {row.pro?.commercialName ?? '—'}
                    </Typography>
                </Box>
            ),
        },
        {
            id: 'brand',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Marque</Typography>,
            field: 'brand',
            minWidth: 160,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                        src={row.brand?.brandLogo?.url || '/default-logo.png'}
                        alt={row.brand?.brandName || 'Brand Logo'}
                        sx={{ width: '20px', height: '20px' }}
                    />
                    <Typography variant="body2" sx={{ color: theme => theme.palette.textSecondary?.main }}>
                        {row.brand?.brandName ?? '—'}
                    </Typography>
                </Box>
            ),
        },
        {
            id: 'city',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Ville</Typography>,
            field: 'city',
            minWidth: 130,
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ color: theme => theme.palette.textSecondary?.main }}>
                    {row.city ?? '—'}
                </Typography>
            ),
        },
        {
            id: 'contractType',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Type de contrat</Typography>,
            field: 'contract',
            minWidth: 150,
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ color: theme => theme.palette.textSecondary?.main }}>
                    {row.contract?.contractType ?? '—'}
                </Typography>
            ),
        },
        {
            id: 'createdDate',
            label: <Typography variant="h5" sx={{ fontSize: '14px', fontWeight: 500 }}>Créé le</Typography>,
            field: 'createdDate',
            minWidth: 130,
            sortable: true,
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ color: theme => theme.palette.textSecondary?.main }}>
                    {row.createdDate
                        ? new Date(row.createdDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '—'}
                </Typography>
            ),
        },
        {
            id: 'actions',
            label: '',
            field: '_id',
            minWidth: 60,
            width: 60,
            renderCell: ({ row }) => (
                <TemplateRowActions row={row} onDeleted={onDeleted} />
            ),
        },
    ], [onDeleted, copied, copiedId, handleCopy]);

    return (
        <Box ref={containerRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <DataTable
                rows={data}
                columns={columns}
                loading={loading}
                collapsible={true}
                collapseAnchorColumn="title"
                rowSkeleton={<TemplateSkeleton />}
                noDataComponent={
                    <EmptyState
                        title="Aucun modèle trouvé"
                        description="Commencez par créer un modèle d'offre d'emploi."
                    />
                }
                pagination={{
                    currentPage,
                    pageSize,
                    totalRows,
                    onPageChange,
                    onPageSizeChange,
                }}
                height={containerHeight ? `${containerHeight}px` : 'calc(100vh - 350px)'}
                stickyHeader
            />
        </Box>
    );
};

export default JobOfferTemplateDataTable;
